import { type ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { GetShip } from "../../App/Context"
import {
  deps,
  many,
  nestedNoteRows,
  useDataStore,
  useDeps,
  useQueryReady,
  type DepsIds,
  type EntityRows,
} from "../../Data"
import type { ICruise, ITrack, ITrip } from "../../Types"
import { Subscription } from "../../Types/Generated/LogbookApi/Subscriptions"
import { SchemaEntities } from "../../Types/Generated/LogbookModel/Schema"
import { CruiseDataContext } from "./DataContext"
import type { CruiseDataProps } from "./Params"
import { createStoreCruiseWriters } from "./writers"
import { useAuthoritativeSeedIds } from "../useAuthoritativeSeedIds"

const CruiseRecordDeps = deps(SchemaEntities.Ship, {
  cruises: many(SchemaEntities.Cruise, { with: ["calculated", "calculatedV2", "notes"] }),
});

const CruiseTripDeps = deps(SchemaEntities.Cruise, {
  trips: many(SchemaEntities.Trip, { with: ["calculated", "calculatedV2", "notes"] }),
});

const CruiseTrackDeps = deps(SchemaEntities.Cruise, {
  tracks: many(SchemaEntities.Track),
});

function nestedRows(
  cruise: ICruise | undefined,
  trips: readonly ITrip[],
  tracks: readonly ITrack[],
): EntityRows {
  const notes = [
    ...(cruise?.notes ?? []),
    ...trips.flatMap((trip) => trip.notes ?? []),
  ];
  return {
    Cruise: cruise ? [cruise] : [],
    Trip: trips,
    Track: tracks,
    ...nestedNoteRows(notes),
  };
}

interface CruiseDataInnerProps extends CruiseDataProps {
  children?: ReactNode;
}

function CruiseDataInner({
  cruiseChildren,
  cruiseId,
  initialCruise,
  initTracks,
  initTrips,
  children,
}: CruiseDataInnerProps) {
  const store = useDataStore();
  const ship = GetShip();
  const cruiseIds = { shipId: ship.id, cruiseId };
  const recordIds: DepsIds<typeof CruiseRecordDeps.relationships> = { shipId: ship.id };
  const tripIds: DepsIds<typeof CruiseTripDeps.relationships> = { shipId: ship.id, cruiseId };
  const trackIds: DepsIds<typeof CruiseTrackDeps.relationships> = { shipId: ship.id, cruiseId };
  const { cruises } = useDeps(CruiseRecordDeps, recordIds, { fetch: false });
  const { trips } = useDeps(CruiseTripDeps, tripIds, { fetch: false });
  const { tracks } = useDeps(CruiseTrackDeps, trackIds, { fetch: false });
  const writers = useMemo(
    () => createStoreCruiseWriters(store, { shipId: ship.id, cruiseId }),
    [store, ship.id, cruiseId],
  );
  const seededTripIds = useAuthoritativeSeedIds(
    initTrips,
    Subscription.Trip,
    (trip) => trip.cruiseId === cruiseId,
  );
  const seededTrackIds = useAuthoritativeSeedIds(
    initTracks,
    Subscription.Track,
    (track) => track.cruiseId === cruiseId,
  );
  const cruiseReady = useQueryReady(
    store,
    SchemaEntities.Cruise,
    cruiseIds,
    initialCruise === undefined,
    initialCruise !== undefined,
  );
  const tripsReady = useQueryReady(
    store,
    SchemaEntities.Trip,
    tripIds,
    !cruiseChildren && initTrips === undefined,
    initTrips !== undefined,
  );
  const tracksReady = useQueryReady(
    store,
    SchemaEntities.Track,
    trackIds,
    initTracks === undefined,
    initTracks !== undefined,
  );

  const resolvedCruise = cruiseReady
    ? (cruises as ICruise[]).find((candidate) => candidate.id === cruiseId)
    : undefined;
  const selectedTrips = seededTripIds
    ? (trips as ITrip[]).filter((trip) => seededTripIds.has(trip.id))
    : trips as ITrip[];
  const selectedTracks = seededTrackIds
    ? (tracks as ITrack[]).filter((track) => seededTrackIds.has(track.id))
    : tracks as ITrack[];
  const resolvedTrips = tripsReady ? selectedTrips : undefined;
  const resolvedTracks = tracksReady ? selectedTracks : undefined;
  const value = useMemo(
    () => ({ cruise: resolvedCruise, trips: resolvedTrips, tracks: resolvedTracks, writers }),
    [resolvedCruise, resolvedTrips, resolvedTracks, writers],
  );

  return <CruiseDataContext.Provider value={value}>{children}</CruiseDataContext.Provider>;
}

// Store-backed CruiseView acquisition (#1621, tracker #665). Incoming props are
// optimistic seeds; normalized store snapshots own every subsequent live update.
export function CruiseDataView(props: CruiseDataProps & { children?: ReactNode }) {
  const store = useDataStore();
  const seed = nestedRows(props.initialCruise, props.initTrips ?? [], props.initTracks ?? []);
  const seedRef = useRef(seed);
  seedRef.current = seed;
  const seedRevision = JSON.stringify(seed);

  useState(() => {
    if (!props.storeBacked) store.applyBundle({ entities: seed });
    return true;
  });

  useEffect(() => {
    if (!props.storeBacked) store.applyBundle({ entities: seedRef.current });
  }, [store, seedRevision, props.storeBacked]);

  return <CruiseDataInner {...props}>{props.children}</CruiseDataInner>;
}
